import './Demographics.scss';

import * as React from 'react';
import { useEffect } from 'react';

import { OrganizationTypes } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
/* eslint-disable react/prop-types */
import { translate } from '~l10n/client';
import { mapToSupportedLocale } from '~l10n/utils/mapToSupportedLocale';
import {
  SUPPORTED_LANGUAGES,
  SupportedLanguageCode,
} from '~shared/SupportedLanguages';
import { accountKeys } from '~teacher/hook/account';
import { proCheck } from '~teacher/util/proCheck';

import * as Constants from '../../../../../../shared/Constants';
import Select from '../../../../../shared/components/elements/Select';
import Tooltip from '../../../../../shared/components/tooltips/Tooltip';
import ProfileTabs from '../../../../teacher/profile/components/ProfileTabs';
import { SchoolSelector } from '../../../../teacher/profile/components/SchoolSelector';

type SelectOption<TValue = string> = {
  title: string;
  value: TValue;
};

const languageOptions: ReadonlyArray<SelectOption<SupportedLanguageCode>> =
  SUPPORTED_LANGUAGES.map(({ code, language }) => ({
    title: language,
    value: code,
  }));

const usOrgTypes: ReadonlyArray<SelectOption> = [
  {
    title: translate('K-12'),
    value: OrganizationTypes.K12,
  },
  {
    title: translate('Higher Ed'),
    value: OrganizationTypes.HigherEd,
  },
];

const internationalOrgTypes: ReadonlyArray<SelectOption<OrganizationTypes>> = [
  {
    title: translate('Primary/Secondary School'),
    value: OrganizationTypes.K12,
  },
  {
    title: translate('University'),
    value: OrganizationTypes.HigherEd,
  },
];

const orgTypeOptions: ReadonlyArray<SelectOption<OrganizationTypes>> = [
  {
    title: translate('Corporate'),
    value: OrganizationTypes.Corporate,
  },
  {
    title: translate('Other'),
    value: OrganizationTypes.Other,
  },
];

const roleOptions: ReadonlyArray<SelectOption> = [
  {
    title: translate('Teacher'),
    value: Constants.ROLE_TEACHER,
  },
  {
    title: translate('Administrator'),
    value: Constants.ROLE_ADMINISTRATOR,
  },
  {
    title: translate('IT/Technology'),
    value: Constants.ROLE_IT_TECHNOLOGY,
  },
  {
    title: translate('Other'),
    value: Constants.ROLE_OTHER,
  },
];

interface Props {
  controller: any;
  model: any;
  languageDisabled: boolean;
  selectedTab: string;
  router: any;
}

export default function Demographics(props: Props) {
  const queryClient = useQueryClient();
  const {
    model: {
      errors,
      showLanguageTooltip,
      orgType,
      country,
      role: [primaryRole],
    },
    controller: {
      disabledLanguageClicked,
      languageChanged,
      orgTypeChanged,
      saveClicked,
    },
    languageDisabled,
  } = props;

  // map to a supported locale in order to show the correctly supported option.
  const language = mapToSupportedLocale(props.model.language);

  const hasPaidPlan = proCheck.hasPaidPlan();

  useEffect(() => {
    new FeatureUseEventAdapter({
      feature: 'demographics_page',
      action: 'open',
    }).sendAnalytics();
  }, []);

  return (
    <div id="profile-container" className="max-w-7xl m-auto">
      <ProfileTabs {...props} />
      <div id="profile-form-container" className="form pl-4 pr-4 md:pr-0">
        <div className="demographics-form-container">
          <div className="input-container profile-select-container profile-language-select">
            <p className="demo-select-label">{translate('Language')}</p>
            <Select
              disabled={languageDisabled}
              error={errors.language.error}
              label={translate('Select Your Language')}
              onSelect={(_name, value: string) => languageChanged(value)}
              onClickDisabled={
                languageDisabled ? disabledLanguageClicked : undefined
              }
            >
              {languageOptions.map(({ title, value }) => ({
                title,
                value,
                selected: language === value,
              }))}
            </Select>
            {showLanguageTooltip && (
              <Tooltip
                location="above"
                title={translate('Activity Running')}
                text={translate(
                  'Language cannot be changed while an activity is running.'
                )}
              />
            )}
          </div>
          <div className="input-container  profile-select-container">
            <p className="demo-select-label">
              {translate('Organization Type')}
            </p>
            <Select
              disabled={hasPaidPlan}
              error={errors.orgType.error}
              label={translate('Select Your Organization Type')}
              onSelect={(_name, value: string) => orgTypeChanged(value)}
            >
              {[
                ...(country === 'US' ? usOrgTypes : internationalOrgTypes),
                ...orgTypeOptions,
              ].map(({ title, value }) => ({
                title,
                value,
                selected: orgType === value,
              }))}
            </Select>
          </div>

          <div className="input-container  profile-select-container">
            <p className="demo-select-label">{translate('Role')}</p>
            <Select
              error={errors.role.error}
              label={translate('Select Your Role')}
              onSelect={(_name, value: string) =>
                props.controller.roleChanged(value)
              }
            >
              {roleOptions.map(({ title, value }) => ({
                title,
                value,
                selected: primaryRole === value,
              }))}
            </Select>
          </div>

          <div />

          <div className="input-container school-container flex flex-col mt-12">
            <p>{translate('School')}</p>
            <SchoolSelector
              legacyModel={props.model}
              legacyController={props.controller}
            />
          </div>
        </div>

        <div className="profile-button-container">
          <button
            id="demo-save-button"
            className="action-button action-button-200"
            onClick={() => {
              queryClient.removeQueries(accountKeys.account);
              saveClicked();
            }}
          >
            {translate('Save', true)}
          </button>
        </div>
      </div>
    </div>
  );
}
